/**
 * Crown01 — Same-day dispatch note for the cart drawer.
 *
 * Honest, conditional copy. Uses UK time (Europe/London) so BST / GMT switches
 * are handled automatically by Intl.DateTimeFormat. The countdown ONLY shows
 * when:
 *   1) Today is Monday–Friday in UK time
 *   2) Today is NOT a UK bank holiday (2026 + 2027 hard-coded list below)
 *   3) Current UK time is before 13:00 (1pm)
 *
 * Otherwise the component renders the "next working day" line. We never claim
 * a guaranteed dispatch — the wording is always "where stock is available".
 *
 * Updates every 60s. No second-level precision (keeps the UI calm).
 */
import React, { useEffect, useState } from "react";

// UK bank holidays (England & Wales). Update this list once a year.
// Source: https://www.gov.uk/bank-holidays
const UK_BANK_HOLIDAYS = new Set([
  // 2026
  "2026-01-01", // New Year's Day
  "2026-04-03", // Good Friday
  "2026-04-06", // Easter Monday
  "2026-05-04", // Early May bank holiday
  "2026-05-25", // Spring bank holiday
  "2026-08-31", // Summer bank holiday
  "2026-12-25", // Christmas Day
  "2026-12-28", // Boxing Day (substitute — 26 Dec falls on a Saturday)
  // 2027
  "2027-01-01", // New Year's Day
  "2027-03-26", // Good Friday
  "2027-03-29", // Easter Monday
  "2027-05-03", // Early May bank holiday
  "2027-05-31", // Spring bank holiday
  "2027-08-30", // Summer bank holiday
  "2027-12-27", // Christmas Day (substitute — 25 Dec falls on a Saturday)
  "2027-12-28", // Boxing Day (substitute — 26 Dec falls on a Sunday)
]);

// Returns the wall-clock components in UK time (Europe/London) for the given JS Date.
function getUkParts(date) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric", month: "2-digit", day: "2-digit",
    weekday: "short", hour: "2-digit", minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(date).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  // weekday is "Mon", "Tue", ..., "Sun"
  const hour = parts.hour === "24" ? 0 : parseInt(parts.hour, 10); // safeguard for some envs
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    weekday: parts.weekday,
    hour,
    minute: parseInt(parts.minute, 10),
    iso: `${parts.year}-${parts.month}-${parts.day}`,
  };
}

function isUkWorkingDay(uk) {
  if (uk.weekday === "Sat" || uk.weekday === "Sun") return false;
  if (UK_BANK_HOLIDAYS.has(uk.iso)) return false;
  return true;
}

export default function DispatchNote() {
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const uk = getUkParts(new Date(tick));
  const workingDay = isUkWorkingDay(uk);
  const beforeCutoff = workingDay && uk.hour < 13;

  let label;
  if (beforeCutoff) {
    const totalMin = (13 - uk.hour) * 60 - uk.minute;
    if (totalMin <= 0) {
      // Edge: at exactly 13:00 — treat as past cutoff.
      label = "Orders placed now will be prepared for the next working day.";
    } else {
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      const countdown = h > 0 ? `${h}h ${m}m` : `${m}m`;
      label = `Order in the next ${countdown} for same-day dispatch where stock is available.`;
    }
  } else {
    label = "Orders placed now will be prepared for the next working day.";
  }

  return (
    <div
      className="flex items-start gap-2 px-1 mb-3"
      data-testid="dispatch-note"
      data-dispatch-window={beforeCutoff ? "before-cutoff" : "after-cutoff"}
      role="note"
    >
      <i
        className="fa-solid fa-circle-check mt-[3px] text-[12px] shrink-0"
        style={{ color: "var(--c-gold-deep)" }}
        aria-hidden="true"
      ></i>
      <p className="text-[11px] leading-snug" style={{ color: "var(--c-grey-500)" }}>
        {label}
      </p>
    </div>
  );
}
